// src/contexts/TripContext.tsx
// src/contexts/TripContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
   collection,
   query,
   where,
   getDocs,
   addDoc,
   updateDoc,
   deleteDoc,
   doc,
   getDoc,
   serverTimestamp,
   Timestamp,
   orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useUser } from './UserContext';
import { useSubscription } from './SubscriptionContext';
import tripSharingService, { TripRole } from '../services/TripSharingService';

// Define Trip interface with sharing permissions
export interface Trip {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   createdBy: string;
   createdAt: Date;
   updatedAt?: Date;
   coverPhotoUrl?: string;
   coverPhotoId?: string;
   locations?: string[]; // IDs of locations
   isPublic?: boolean;
   userRole?: TripRole; // Current user's role for this trip
   isFromAcceptedInvitation?: boolean; // Added for accepted invitations
   primaryDestination?: {
      name: string;
      coordinates: [number, number]; // [longitude, latitude]
   };
}

interface Location {
   id: string;
   name: string;
   coordinates: [number, number];
   description?: string;
   country?: string;
   type?: 'city' | 'landmark' | 'nature' | 'accommodation' | 'activity';
   tags?: string[];
}

interface Photo {
   id: string;
   url: string;
   thumbnailUrl: string;
   title?: string;
   description?: string;
   takenAt?: Date;
   locationId?: string;
   tripId?: string;
   tags?: string[];
   uploadedBy: string;
   uploadedAt: Date;
}

interface Comment {
   id: string;
   text: string;
   createdBy: string;
   createdAt: Date;
   updatedAt?: Date;
   entityId: string; // ID of the trip, photo, or location being commented on
   entityType: 'trip' | 'photo' | 'location';
}

// Trip creation/edit form data
export interface TripFormData {
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   coverPhotoUrl?: string;
   coverPhotoId?: string;
   isPublic?: boolean;
}

interface TripContextType {
   trips: Trip[];
   sharedTrips: Trip[];
   loading: boolean;
   error: string | null;
   isSuperAdmin: boolean;
   createTrip: (tripData: TripFormData) => Promise<string>;
   updateTrip: (tripId: string, tripData: Partial<TripFormData>) => Promise<void>;
   deleteTrip: (tripId: string) => Promise<void>;
   getTripById: (tripId: string) => Promise<Trip | null>;
   fetchTrips: () => Promise<void>;
   addTripComment: (tripId: string, text: string) => Promise<string>;
   getTripComments: (tripId: string) => Promise<Comment[]>;
   checkTripPermission: (tripId: string, requiredRole?: TripRole) => Promise<boolean>;
   addPhotoToTrip: (
      tripId: string,
      photoData: Omit<Photo, 'id' | 'uploadedBy' | 'uploadedAt'>
   ) => Promise<string>;
   getTripPhotos: (tripId: string) => Promise<Photo[]>;
   getUserPhotos: (userId: string, tripId?: string) => Promise<Photo[]>;
   checkSuperAdminStatus: () => boolean;
}

const TripContext = createContext<TripContextType | undefined>(undefined);

// Super admin configuration (should match AuthGuard)
const SUPER_ADMIN_EMAILS = ['emmanueljones05@gmail.com'];

export const TripProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [trips, setTrips] = useState<Trip[]>([]);
   const [sharedTrips, setSharedTrips] = useState<Trip[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [isSuperAdmin, setIsSuperAdmin] = useState(false);
   const { currentUser, isAuthenticated } = useUser();

   // Check if the current user is a super admin
   const checkSuperAdminStatus = (): boolean => {
      if (!currentUser) return false;
      return currentUser.email ? SUPER_ADMIN_EMAILS.includes(currentUser.email) : false;
   };

   // Update super admin status when user changes
   useEffect(() => {
      if (currentUser) {
         const isAdmin = checkSuperAdminStatus();
         setIsSuperAdmin(isAdmin);

         // Store in localStorage for quicker access across the app
         if (isAdmin) {
            localStorage.setItem('isSuperAdmin', 'true');
         } else {
            localStorage.removeItem('isSuperAdmin');
         }
      } else {
         setIsSuperAdmin(false);
         localStorage.removeItem('isSuperAdmin');
      }
   }, [currentUser]);

   // Fetch user's trips when authenticated
   useEffect(() => {
      if (isAuthenticated && currentUser) {
         fetchTrips();
      } else {
         // Reset trips when logged out
         setTrips([]);
         setSharedTrips([]);
      }
   }, [isAuthenticated, currentUser]);

   // Fetch all trips the user has access to
   const fetchTrips = async () => {
      if (!currentUser) return;

      setLoading(true);
      setError(null);

      try {
         const tripsSnapshot = await getDocs(collection(db, 'trips'));

         const tripsData: Trip[] = [];

         tripsSnapshot.forEach((doc) => {
            const data = doc.data();

            // Check if each trip has the createdBy or owner field
            const ownerId = data.owner || null;
            const isOwner = ownerId === currentUser?.uid;
            const isShared =
               data.collaborators &&
               Array.isArray(data.collaborators) &&
               data.collaborators.some((c: any) => c?.email === currentUser?.email);

            if (isOwner || isSuperAdmin || isShared) {
               // Convert Firebase timestamps to Date objects safely
               let startDate, endDate, createdAt, updatedAt;

               try {
                  startDate = data.startDate?.toDate ? data.startDate.toDate() : new Date();
               } catch (e) {
                  startDate = new Date();
               }

               try {
                  endDate = data.endDate?.toDate ? data.endDate.toDate() : new Date();
               } catch (e) {
                  endDate = new Date();
               }

               try {
                  createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
               } catch (e) {
                  createdAt = new Date();
               }

               try {
                  updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined;
               } catch (e) {
                  updatedAt = undefined;
               }

               const trip: Trip = {
                  ...data,
                  id: doc.id,
                  title: data.title,
                  description: data.description || '',
                  startDate,
                  endDate,
                  createdAt,
                  updatedAt,
                  coverPhotoUrl: data.coverPhotoUrl,
                  userRole: isOwner ? 'owner' : 'viewer',
                  createdBy: data.createdBy || currentUser.uid,
               };

               tripsData.push(trip);
            } else {
            }
         });

         setTrips(tripsData);

         setSharedTrips([]);
      } catch (err) {
         console.error('Error fetching trips:', err);
         setError('Failed to load trips');
      } finally {
         setLoading(false);
      }
   };

   const createTrip = async (tripData: TripFormData): Promise<string> => {
      if (!currentUser) throw new Error('User must be authenticated to create a trip');

      // Super admins can always create trips
      const isAdmin = checkSuperAdminStatus();

      setLoading(true);

      try {
         // Call backend API to create trip
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/trips`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               ...tripData,
               userId: currentUser.uid,
               startDate: tripData.startDate.toISOString(),
               endDate: tripData.endDate.toISOString(),
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         // Refresh trips list
         await fetchTrips();

         return result.tripId;
      } catch (err) {
         console.error('Error creating trip:', err);
         setError('Failed to create trip');
         throw err;
      } finally {
         setLoading(false);
      }
   };

   const updateTrip = async (tripId: string, tripData: Partial<TripFormData>): Promise<void> => {
      if (!currentUser) throw new Error('User must be authenticated to update a trip');

      // Super admin can edit any trip, others need permission
      let hasPermission = isSuperAdmin;

      if (!hasPermission) {
         hasPermission = await checkTripPermission(tripId, 'editor');
      }

      if (!hasPermission) {
         throw new Error('You do not have permission to edit this trip');
      }

      setLoading(true);

      try {
         const updateData: any = {
            ...tripData,
            updatedAt: serverTimestamp(),
         };

         // Convert dates to Firebase timestamps
         if (tripData.startDate) {
            updateData.startDate = Timestamp.fromDate(tripData.startDate);
         }

         if (tripData.endDate) {
            updateData.endDate = Timestamp.fromDate(tripData.endDate);
         }

         await updateDoc(doc(db, 'trips', tripId), updateData);

         // Refresh trips list
         await fetchTrips();
      } catch (err) {
         console.error('Error updating trip:', err);
         setError('Failed to update trip');
         throw err;
      } finally {
         setLoading(false);
      }
   };

   const deleteTrip = async (tripId: string) => {
      if (!currentUser) throw new Error('User must be authenticated to delete a trip');

      // Super admin can delete any trip, others need owner permission
      let hasPermission = isSuperAdmin;

      if (!hasPermission) {
         hasPermission = await checkTripPermission(tripId, 'owner');
      }

      if (!hasPermission) {
         throw new Error('You do not have permission to delete this trip');
      }

      setLoading(true);

      try {
         // Use backend API to delete trip
         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl(`/api/trips/${tripId}?userId=${currentUser?.uid}`);

         const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers,
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to delete trip');
         }

         // Refresh trips list
         await fetchTrips();
      } catch (err) {
         console.error('Error deleting trip:', err);
         setError('Failed to delete trip');
      } finally {
         setLoading(false);
      }
   };

   const getTripById = async (tripId: string): Promise<Trip | null> => {
      if (!currentUser) throw new Error('User must be authenticated to get a trip');

      setLoading(true);
      setError(null);

      try {
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!tripDoc.exists()) {
            return null;
         }

         const data = tripDoc.data();

         // Check if user has access to this trip (super admin has access to all)
         if (!isSuperAdmin) {
            const { hasAccess, role } = await tripSharingService.checkTripAccess(
               tripId,
               currentUser.uid
            );

            if (!hasAccess) {
               // Check if it's the user's own trip
               if (data.createdBy !== currentUser.uid) {
                  throw new Error('You do not have access to this trip');
               }
            }
         }

         return {
            id: tripDoc.id,
            title: data.title,
            description: data.description,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            coverPhotoUrl: data.coverPhotoUrl,
            coverPhotoId: data.coverPhotoId,
            locations: data.locations,
            isPublic: data.isPublic || false,
            userRole: data.createdBy === currentUser.uid ? 'owner' : data.userRole || 'viewer',
         };
      } catch (err) {
         console.error('Error getting trip:', err);
         setError('Failed to get trip');
         return null;
      } finally {
         setLoading(false);
      }
   };

   const addTripComment = async (tripId: string, text: string): Promise<string> => {
      if (!currentUser) throw new Error('User must be authenticated to add a comment to a trip');

      // Check if user has access to this trip first
      if (!isSuperAdmin) {
         const { hasAccess } = await tripSharingService.checkTripAccess(tripId, currentUser.uid);
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!hasAccess && (!tripDoc.exists() || tripDoc.data().createdBy !== currentUser.uid)) {
            throw new Error('You do not have access to this trip');
         }
      }

      setLoading(true);

      try {
         const commentData = {
            text,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
            entityId: tripId,
            entityType: 'trip',
         };

         const commentRef = await addDoc(collection(db, 'tripComments'), commentData);

         return commentRef.id;
      } catch (err) {
         console.error('Error adding comment to trip:', err);
         setError('Failed to add comment to trip');
         throw err;
      } finally {
         setLoading(false);
      }
   };

   const getTripComments = async (tripId: string): Promise<Comment[]> => {
      if (!currentUser) throw new Error('User must be authenticated to get comments for a trip');

      // Check if user has access to this trip first
      if (!isSuperAdmin) {
         const { hasAccess } = await tripSharingService.checkTripAccess(tripId, currentUser.uid);
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!hasAccess && (!tripDoc.exists() || tripDoc.data().createdBy !== currentUser.uid)) {
            throw new Error('You do not have access to this trip');
         }
      }

      setLoading(true);
      setError(null);

      try {
         const commentsQuery = query(
            collection(db, 'tripComments'),
            where('entityId', '==', tripId),
            where('entityType', '==', 'trip')
         );

         const commentsSnapshot = await getDocs(commentsQuery);
         const comments: Comment[] = [];

         commentsSnapshot.forEach((doc) => {
            const data = doc.data();
            comments.push({
               id: doc.id,
               text: data.text,
               createdBy: data.createdBy,
               createdAt: data.createdAt.toDate(),
               updatedAt: data.updatedAt?.toDate(),
               entityId: data.entityId,
               entityType: data.entityType,
            });
         });

         return comments;
      } catch (err) {
         console.error('Error getting comments for trip:', err);
         setError('Failed to get comments for trip');
         return [];
      } finally {
         setLoading(false);
      }
   };

   const checkTripPermission = async (
      tripId: string,
      requiredRole?: TripRole
   ): Promise<boolean> => {
      if (!currentUser) return false;

      // Super admin has all permissions
      if (isSuperAdmin) return true;

      try {
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!tripDoc.exists()) {
            return false;
         }

         const tripData = tripDoc.data();

         // If user is the trip creator, they have owner permissions
         if (tripData.createdBy === currentUser.uid) {
            return true; // Trip creator always has full access regardless of requiredRole
         }

         // Otherwise, check permissions from the sharing service
         const { hasAccess, role } = await tripSharingService.checkTripAccess(
            tripId,
            currentUser.uid
         );

         if (!hasAccess) {
            return false;
         }

         if (!requiredRole) {
            return true;
         }

         // Check if user's role meets the required level
         // Editors now have the same access as owners
         if (role === 'owner' || role === 'editor') return true;
         if (role === 'viewer' && requiredRole === 'viewer') return true;

         return false;
      } catch (err) {
         console.error('Error checking trip permission:', err);
         return false;
      }
   };

   const addPhotoToTrip = async (
      tripId: string,
      photoData: Omit<Photo, 'id' | 'uploadedBy' | 'uploadedAt'>
   ): Promise<string> => {
      if (!currentUser) throw new Error('User must be authenticated to add a photo to a trip');

      // Check if user has access to this trip first
      if (!isSuperAdmin) {
         const { hasAccess, role } = await tripSharingService.checkTripAccess(
            tripId,
            currentUser.uid
         );
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         // Must have access to the trip (owner, editor, or viewer can all upload photos)
         if (!hasAccess) {
            throw new Error('You do not have permission to add photos to this trip');
         }
      }

      setLoading(true);

      try {
         const photoDoc = {
            ...photoData,
            uploadedBy: currentUser.uid,
            uploadedAt: serverTimestamp(),
            tripId,
         };

         const photoRef = await addDoc(collection(db, 'photos'), photoDoc);

         return photoRef.id;
      } catch (err) {
         console.error('Error adding photo to trip:', err);
         setError('Failed to add photo to trip');
         throw err;
      } finally {
         setLoading(false);
      }
   };

   const getTripPhotos = async (tripId: string): Promise<Photo[]> => {
      try {
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!tripDoc.exists()) {
            return [];
         }

         const photosQuery = query(collection(db, 'photos'), where('tripId', '==', tripId));

         const photosSnapshot = await getDocs(photosQuery);
         const photos: any[] = [];

         photosSnapshot.forEach((doc) => {
            const data = doc.data();

            photos.push({
               ...data,
               id: doc.id,
               takenAt: data.takenAt?.toDate ? data.takenAt.toDate() : undefined,
               uploadedAt: data.uploadedAt?.toDate ? data.uploadedAt.toDate() : new Date(),
            });
         });

         return photos;
      } catch (err) {
         throw err;
      }
   };

   const getUserPhotos = async (userId: string, tripId?: string): Promise<Photo[]> => {
      if (!currentUser) throw new Error('User must be authenticated to get photos for a user');

      // Super admin can view any user's photos
      // Other users can only view their own photos
      if (!isSuperAdmin && userId !== currentUser.uid) {
         throw new Error('You do not have permission to view photos from this user');
      }

      setLoading(true);
      setError(null);

      try {
         let photosQuery;

         if (tripId) {
            // If specific trip requested, check permissions for that trip first
            if (!isSuperAdmin && userId !== currentUser.uid) {
               const { hasAccess } = await tripSharingService.checkTripAccess(
                  tripId,
                  currentUser.uid
               );
               const tripDoc = await getDoc(doc(db, 'trips', tripId));

               if (
                  !hasAccess &&
                  (!tripDoc.exists() || tripDoc.data().createdBy !== currentUser.uid)
               ) {
                  throw new Error('You do not have access to view photos for this trip');
               }
            }

            photosQuery = query(
               collection(db, 'photos'),
               where('uploadedBy', '==', userId),
               where('tripId', '==', tripId)
            );
         } else {
            photosQuery = query(collection(db, 'photos'), where('uploadedBy', '==', userId));
         }

         const photosSnapshot = await getDocs(photosQuery);
         const photos: Photo[] = [];

         // If not super admin and not viewing own photos, filter by trips user has access to
         if (!isSuperAdmin && userId !== currentUser.uid) {
            const accessibleTripIds = new Set<string>();

            // Add own trips
            const ownTripsQuery = query(
               collection(db, 'trips'),
               where('createdBy', '==', currentUser.uid)
            );

            const ownTripsSnapshot = await getDocs(ownTripsQuery);
            ownTripsSnapshot.forEach((doc) => accessibleTripIds.add(doc.id));

            // Add shared trips
            const sharedTripIds = await tripSharingService.getUserTrips(currentUser.uid);
            sharedTripIds.forEach((id) => accessibleTripIds.add(id));

            // Filter photos by accessible trips
            photosSnapshot.forEach((doc) => {
               const data = doc.data();
               if (accessibleTripIds.has(data.tripId)) {
                  photos.push({
                     id: doc.id,
                     url: data.url,
                     thumbnailUrl: data.thumbnailUrl,
                     title: data.title,
                     description: data.description,
                     takenAt: data.takenAt?.toDate(),
                     locationId: data.locationId,
                     tripId: data.tripId,
                     tags: data.tags,
                     uploadedBy: data.uploadedBy,
                     uploadedAt: data.uploadedAt.toDate(),
                  });
               }
            });
         } else {
            // Super admin or user viewing own photos
            photosSnapshot.forEach((doc) => {
               const data = doc.data();
               photos.push({
                  id: doc.id,
                  url: data.url,
                  thumbnailUrl: data.thumbnailUrl,
                  title: data.title,
                  description: data.description,
                  takenAt: data.takenAt?.toDate(),
                  locationId: data.locationId,
                  tripId: data.tripId,
                  tags: data.tags,
                  uploadedBy: data.uploadedBy,
                  uploadedAt: data.uploadedAt.toDate(),
               });
            });
         }

         return photos;
      } catch (err) {
         console.error('Error getting photos for user:', err);
         setError('Failed to get photos for user');
         return [];
      } finally {
         setLoading(false);
      }
   };

   return (
      <TripContext.Provider
         value={{
            trips,
            sharedTrips,
            loading,
            error,
            isSuperAdmin,
            createTrip,
            updateTrip,
            deleteTrip,
            getTripById,
            fetchTrips,
            addTripComment,
            getTripComments,
            checkTripPermission,
            addPhotoToTrip,
            getTripPhotos,
            getUserPhotos,
            checkSuperAdminStatus,
         }}
      >
         {children}
      </TripContext.Provider>
   );
};

export const useTrip = () => {
   const context = useContext(TripContext);
   if (context === undefined) {
      throw new Error('useTrip must be used within a TripProvider');
   }
   return context;
};
